import { type Getter, type Setter, atom } from "jotai";
import type { EditorState, Plugin } from "prosemirror-state";

import { parsePage, serializePage } from "@moment/api-collab/markdown";
import { Logger } from "@moment/logging";

import { type DocsListItem, docsListItemLocalEntry } from "../document-list";
import {
	$ensurePrimaryAgentInstructionFile,
	agentInstructionPersistFilePaths,
	isAgentInstructionFilename,
	isAgentInstructionPage,
	isLegacyUnwrappedTemplate,
	isPrimaryAgentInstructionPage,
	primaryAgentInstructionFilePath,
	wrapLegacyTemplate,
} from "../document/agents-md.atom";
import { env } from "../env";
import { fs } from "../filesystem";
import { getPageFilePath } from "../paths";
import { $loadEditorState, $setEditorStateError } from "./editor-state.atom";
import { PmDiskLoadError } from "./errors";
import { resolveCurrentPagePath } from "./util";

const logger = new Logger("load-editor-state-from-disk");

const fsApi = fs();

export interface LoadEditorStateFromDiskInput {
	key: string;
	document: DocsListItem;
	pageId: string;
	/** Optional explicit file path relative to document root (for imported documents) */
	pagePath?: string;
	plugins: Plugin[];
}

/**
 * Loads an editor state from disk and initializes it via $loadEditorState.
 */
export const $loadEditorStateFromDisk = atom(
	null,
	async (get, set, input: LoadEditorStateFromDiskInput) => {
		const { key, document, pageId, pagePath, plugins } = input;
		const documentId = document.id;

		try {
			if (!env.isDesktopApp) {
				throw new Error("Disk loading only available in desktop app");
			}

			// Get the document path from the document.
			const documentPath = docsListItemLocalEntry(document)?.path;
			if (!documentPath) {
				throw new Error("Document has no local path");
			}

			// Read and parse the markdown file.
			const pageFilePath = await getPageFilePath(documentPath, pageId, pagePath);
			const resolvedFilename = pageFilePath.split("/").pop()?.toLowerCase();
			const isAgentFile = pagePath
				? isAgentInstructionPage(pagePath)
				: isAgentInstructionFilename(resolvedFilename);
			const isPrimaryAgentFile =
				isPrimaryAgentInstructionPage(pagePath) ||
				pageFilePath === primaryAgentInstructionFilePath(documentPath);
			const rawMarkdown = await get(fsApi.readTextFile({ filePath: pageFilePath })).catch(
				async (e) => {
					if (typeof e !== "string" || !e.includes("os error 2")) {
						throw e;
					}

					if (isPrimaryAgentFile) {
						const ensureResult = await set($ensurePrimaryAgentInstructionFile, {
							documentId,
						});
						if (ensureResult === "created" || ensureResult === "already-exists") {
							return get(fsApi.readTextFile({ filePath: pageFilePath }));
						}
						if (ensureResult === "failed" || ensureResult === "unavailable") {
							throw new Error("Failed to ensure AGENTS.md before loading it");
						}
					}

					logger.info("Page file not found, creating empty file", {
						documentId,
						pageId,
						pageFilePath,
					});

					// Create parent directory first
					const parentDir = await get(fsApi.dirname(pageFilePath));
					await set(fsApi.mkdir, { dirPath: parentDir, options: { recursive: true } });

					await set(fsApi.create, { filePath: pageFilePath });
					return "";
				}
			);

			logger.info("Agent file detection for disk load", {
				key,
				pagePath,
				pageFilePath,
				resolvedFilename,
				isAgentFile,
			});

			// The managed section (if any) is wrapped in <section readonly> on disk
			// and parsed into a readonly_section ProseMirror node that is hidden
			// from the editor. No runtime stripping or merging is needed — the
			// content round-trips through ProseMirror serialization.
			//
			// Pre-v17 agent files had no readonly wrapper — the whole file was
			// template content. Wrap them on load so they render as a hidden
			// managed section, giving the user the same "A newer version is
			// available" affordance via the banner's Update action.
			const markdown =
				isAgentFile && isLegacyUnwrappedTemplate(rawMarkdown)
					? wrapLegacyTemplate(rawMarkdown)
					: rawMarkdown;

			const { doc } = parsePage(markdown);

			// Create disk persistence callback.
			const onPersist = async (
				injectedGet: Getter,
				injectedSet: Setter,
				state: EditorState
			): Promise<void> => {
				const currentPagePath = await resolveCurrentPagePath(
					injectedGet,
					documentId,
					pageId,
					pagePath
				);
				const currentPageFilePath = await getPageFilePath(
					documentPath,
					pageId,
					currentPagePath
				);
				const currentFilename = currentPageFilePath.split("/").pop()?.toLowerCase();
				const currentIsAgentFile = currentPagePath
					? isAgentInstructionPage(currentPagePath)
					: isAgentInstructionFilename(currentFilename);
				const currentPersistFilePaths = currentIsAgentFile
					? agentInstructionPersistFilePaths(documentPath, currentPageFilePath)
					: [currentPageFilePath];
				const currentParentDir = await injectedGet(fsApi.dirname(currentPageFilePath));
				const content = serializePage(state.doc);

				await injectedSet(fsApi.mkdir, {
					dirPath: currentParentDir,
					options: { recursive: true },
				});
				await Promise.all(
					currentPersistFilePaths.map((filePath) =>
						injectedSet(fsApi.writeTextFile, {
							filePath,
							content,
							documentId,
						})
					)
				);
			};

			logger.info("Loading editor state from disk", {
				key,
				documentId,
				pageId,
				pageFilePath,
			});
			// Load the editor state. NEVER SSR, obviously, as this is from disk.
			set($loadEditorState, { key, doc, plugins, ssr: false, onPersist });
		} catch (err) {
			const error = new PmDiskLoadError(err);
			logger.error("Failed to load editor state from disk", { documentId, pageId, err });
			set($setEditorStateError, { key, error });
		}
	}
);
$loadEditorStateFromDisk.debugLabel = "loadEditorStateFromDisk";
